import * as React from "react";
import {MutableRefObject, useEffect, useRef} from "react";
import {useDispatch} from "react-redux";
import {RouteComponentProps, withRouter} from "react-router";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {calculateRemSize} from "@pg-design/helpers-css";
import {FiltersIcon, ListIcon, MapIcon, SearchIcon, SortIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {BUTTONS_TEST_ID} from "@pg-mono/test-ids";

import {useBottomNavBarOffset} from "../../hooks/useBottomNavBarOffset";
import {NewsletterFlowWithRegistrationTrigger} from "../../newsletter/components/NewsletterFlowWithRegistrationTrigger";
import {NotificationVariant} from "../../newsletter/types/NotificationVariant";
import {useIsOfferModalOpen} from "../../offer/hooks/use_is_offer_modal_open";
import {setBottomFixedElementHeight} from "../../ui/actions/ui_actions";
import {ILatestQuery} from "../../utils/latest_query";
import {mobileSearchTriggerHeight} from "../constants/mobile_search_trigger_height";

interface IOwnProps extends RouteComponentProps<{}> {}

interface IProps extends IOwnProps {
    selectedRegionsCount?: number;
    selectedFiltersCount: number;
    onToggleSearchClick?: () => void;
    onToggleFiltersClick: () => void;
    onToggleSortClick: (() => void) | null;
    listUrl?: string;
    mapUrl?: string;
    latestQuery: ILatestQuery;
    onMount?: (searchTriggerRef: MutableRefObject<HTMLDivElement | null>) => void;
    positionFixed?: boolean;
    hideBottomPadding?: boolean;
    /** Set to false for a copy that must not carry the shared filters entry test id (the owner of the id is another copy). */
    hasFiltersEntryTestId?: boolean;
}

const mobileSearchTriggersWrapperHeight = 64;

const MobileSearchTriggersC = (props: IProps) => {
    const dispatch = useDispatch();
    const isOfferModalOpen = useIsOfferModalOpen();
    const {hasFiltersEntryTestId = true} = props;

    const searchTriggerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        props.onMount && props.onMount(searchTriggerRef);
    }, []);

    const theme = useTheme();

    const onSortClick = () => {
        props.onToggleSortClick && props.onToggleSortClick();
    };

    !props.hideBottomPadding && useBottomNavBarOffset();

    useEffect(() => {
        dispatch(setBottomFixedElementHeight(mobileSearchTriggersWrapperHeight));
    }, []);

    return (
        <MobileSearchTriggersWrapper positionFixed={props.positionFixed}>
            <div css={triggersHolder}>
                {props.onToggleSearchClick && (
                    <div css={triggerIconHolder} onClick={props.onToggleSearchClick} ref={searchTriggerRef}>
                        <SearchIcon size="1.8" />
                        <Text variant="info_txt_1">Szukaj</Text>
                        {props.selectedRegionsCount ? <div css={triggerBadge}>{props.selectedRegionsCount}</div> : ""}
                    </div>
                )}
                <div
                    css={triggerIconHolder}
                    onClick={props.onToggleFiltersClick}
                    data-testid={hasFiltersEntryTestId && !isOfferModalOpen ? BUTTONS_TEST_ID.OPEN_FILTERS_BUTTON : undefined}
                >
                    <FiltersIcon size="1.8" />
                    <Text variant="info_txt_1">Filtruj</Text>
                    {props.selectedFiltersCount ? <div css={triggerBadge}>{props.selectedFiltersCount}</div> : ""}
                </div>

                <TriggerSortHolder active={!!props.onToggleSortClick} onClick={onSortClick}>
                    <SortIcon size="1.8" fill={props.onToggleSortClick ? theme.colors.gray[700] : theme.colors.gray[300]} />
                    <Text variant="info_txt_1">Sortuj</Text>
                </TriggerSortHolder>

                {props.listUrl && (
                    <a href={props.listUrl} css={triggerIconHolder}>
                        <ListIcon size="1.8" />
                        <Text variant="info_txt_1">Lista</Text>
                    </a>
                )}
                {!props.listUrl && props.mapUrl && (
                    <a href={props.mapUrl} css={triggerIconHolder}>
                        <MapIcon size="1.8" />
                        <Text variant="info_txt_1">Mapa</Text>
                    </a>
                )}
                {props.latestQuery?.region && (
                    <div css={triggerIconHolder}>
                        <NewsletterFlowWithRegistrationTrigger variant={NotificationVariant.MOBILE} amplitudePlacement="rp_save_alert_search_bar_filters" />
                        <Text variant="info_txt_1" css={textHolder}>
                            Powiadomienia
                        </Text>
                    </div>
                )}
            </div>
        </MobileSearchTriggersWrapper>
    );
};

export const MobileSearchTriggers = withRouter(MobileSearchTriggersC);

const MobileSearchTriggersWrapper = styled.div<{positionFixed?: boolean}>`
    ${(props) =>
        props.positionFixed &&
        css`
            position: fixed;
            z-index: 1001;
            bottom: 0;
            left: 0;
        `}
    width: 100%;
    background: #fff;
    box-shadow: 0 0.2rem 2rem rgba(0, 0, 0, 0.2);
    height: ${mobileSearchTriggerHeight};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        display: none;
    }
`;

const triggersHolder = () => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem 1rem;
    max-width: 36rem;
    margin: 0 auto;
`;

const triggerIconHolder = (theme: Theme) => css`
    color: ${theme.colors.secondary};
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: default;
    &:visited,
    &:hover,
    &:focus,
    &:active {
        color: ${theme.colors.secondary};
        text-decoration: none;
    }

    svg {
        margin-bottom: 1rem;
    }
`;

interface ITriggerSortHolder {
    active: boolean;
}

const TriggerSortHolder = styled.div<ITriggerSortHolder>`
    ${(props) => triggerIconHolder(props.theme)}
    ${(props) =>
        !props.active &&
        css`
            color: ${props.theme.colors.gray[300]};

            &:visited,
            &:hover,
            &:focus,
            &:active {
                color: ${props.theme.colors.gray[700]};
                text-decoration: none;
            }
        `};
`;

const triggerBadge = (theme: Theme) => css`
    position: absolute;
    top: -1rem;
    left: -0.2rem;
    color: ${theme.colors.gray[700]};
    background: ${theme.colors.primary};
    font-size: ${theme.globals.text.root};
    border-radius: 50%;
    border: 1px solid #fff;
    width: 1.6rem;
    height: 1.6rem;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const textHolder = css`
    width: ${calculateRemSize(5)};
    text-overflow: ellipsis;
    overflow: hidden;
`;
import * as React from "react";
import {MutableRefObject, useEffect, useRef} from "react";
import {useDispatch} from "react-redux";
import {RouteComponentProps, withRouter} from "react-router";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {calculateRemSize} from "@pg-design/helpers-css";
import {FiltersIcon, ListIcon, MapIcon, SearchIcon, SortIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {BUTTONS_TEST_ID} from "@pg-mono/test-ids";

import {useBottomNavBarOffset} from "../../hooks/useBottomNavBarOffset";
import {NewsletterFlowWithRegistrationTrigger} from "../../newsletter/components/NewsletterFlowWithRegistrationTrigger";
import {NotificationVariant} from "../../newsletter/types/NotificationVariant";
import {useIsOfferModalOpen} from "../../offer/hooks/use_is_offer_modal_open";
import {setBottomFixedElementHeight} from "../../ui/actions/ui_actions";
import {ILatestQuery} from "../../utils/latest_query";
import {mobileSearchTriggerHeight} from "../constants/mobile_search_trigger_height";

interface IOwnProps extends RouteComponentProps<{}> {}

interface IProps extends IOwnProps {
    selectedRegionsCount?: number;
    selectedFiltersCount: number;
    onToggleSearchClick?: () => void;
    onToggleFiltersClick: () => void;
    onToggleSortClick: (() => void) | null;
    listUrl?: string;
    mapUrl?: string;
    latestQuery: ILatestQuery;
    onMount?: (searchTriggerRef: MutableRefObject<HTMLDivElement | null>) => void;
    positionFixed?: boolean;
    hideBottomPadding?: boolean;
    /** Set to false for a copy that must not carry the shared filters entry test id (the owner of the id is another copy). */
    hasFiltersEntryTestId?: boolean;
}

const mobileSearchTriggersWrapperHeight = 64;

const MobileSearchTriggersC = (props: IProps) => {
    const dispatch = useDispatch();
    const isOfferModalOpen = useIsOfferModalOpen();
    const {hasFiltersEntryTestId = true} = props;

    const searchTriggerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        props.onMount && props.onMount(searchTriggerRef);
    }, []);

    const theme = useTheme();

    const onSortClick = () => {
        props.onToggleSortClick && props.onToggleSortClick();
    };

    !props.hideBottomPadding && useBottomNavBarOffset();

    useEffect(() => {
        dispatch(setBottomFixedElementHeight(mobileSearchTriggersWrapperHeight));
    }, []);

    return (
        <MobileSearchTriggersWrapper positionFixed={props.positionFixed}>
            <div css={triggersHolder}>
                {props.onToggleSearchClick && (
                    <div css={triggerIconHolder} onClick={props.onToggleSearchClick} ref={searchTriggerRef}>
                        <SearchIcon size="1.8" />
                        <Text variant="info_txt_1">Szukaj</Text>
                        {props.selectedRegionsCount ? <div css={triggerBadge}>{props.selectedRegionsCount}</div> : ""}
                    </div>
                )}
                <div
                    css={triggerIconHolder}
                    onClick={props.onToggleFiltersClick}
                    data-testid={hasFiltersEntryTestId && !isOfferModalOpen ? BUTTONS_TEST_ID.OPEN_FILTERS_BUTTON : undefined}
                >
                    <FiltersIcon size="1.8" />
                    <Text variant="info_txt_1">Filtruj</Text>
                    {props.selectedFiltersCount ? <div css={triggerBadge}>{props.selectedFiltersCount}</div> : ""}
                </div>

                <TriggerSortHolder active={!!props.onToggleSortClick} onClick={onSortClick}>
                    <SortIcon size="1.8" fill={props.onToggleSortClick ? theme.colors.gray[700] : theme.colors.gray[300]} />
                    <Text variant="info_txt_1">Sortuj</Text>
                </TriggerSortHolder>

                {props.listUrl && (
                    <a href={props.listUrl} css={triggerIconHolder}>
                        <ListIcon size="1.8" />
                        <Text variant="info_txt_1">Lista</Text>
                    </a>
                )}
                {!props.listUrl && props.mapUrl && (
                    <a href={props.mapUrl} css={triggerIconHolder}>
                        <MapIcon size="1.8" />
                        <Text variant="info_txt_1">Mapa</Text>
                    </a>
                )}
                {props.latestQuery?.region && (
                    <div css={triggerIconHolder}>
                        <NewsletterFlowWithRegistrationTrigger variant={NotificationVariant.MOBILE} amplitudePlacement="rp_save_alert_search_bar_filters" />
                        <Text variant="info_txt_1" css={textHolder}>
                            Powiadomienia
                        </Text>
                    </div>
                )}
            </div>
        </MobileSearchTriggersWrapper>
    );
};

export const MobileSearchTriggers = withRouter(MobileSearchTriggersC);

const MobileSearchTriggersWrapper = styled.div<{positionFixed?: boolean}>`
    ${(props) =>
        props.positionFixed &&
        css`
            position: fixed;
            z-index: 1001;
            bottom: 0;
            left: 0;
        `}
    width: 100%;
    background: #fff;
    box-shadow: 0 0.2rem 2rem rgba(0, 0, 0, 0.2);
    height: ${mobileSearchTriggerHeight};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        display: none;
    }
`;

const triggersHolder = () => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem 1rem;
    max-width: 36rem;
    margin: 0 auto;
`;

const triggerIconHolder = (theme: Theme) => css`
    color: ${theme.colors.secondary};
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: default;
    &:visited,
    &:hover,
    &:focus,
    &:active {
        color: ${theme.colors.secondary};
        text-decoration: none;
    }

    svg {
        margin-bottom: 1rem;
    }
`;

interface ITriggerSortHolder {
    active: boolean;
}

const TriggerSortHolder = styled.div<ITriggerSortHolder>`
    ${(props) => triggerIconHolder(props.theme)}
    ${(props) =>
        !props.active &&
        css`
            color: ${props.theme.colors.gray[300]};

            &:visited,
            &:hover,
            &:focus,
            &:active {
                color: ${props.theme.colors.gray[700]};
                text-decoration: none;
            }
        `};
`;

const triggerBadge = (theme: Theme) => css`
    position: absolute;
    top: -1rem;
    left: -0.2rem;
    color: ${theme.colors.gray[700]};
    background: ${theme.colors.primary};
    font-size: ${theme.globals.text.root};
    border-radius: 50%;
    border: 1px solid #fff;
    width: 1.6rem;
    height: 1.6rem;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const textHolder = css`
    width: ${calculateRemSize(5)};
    text-overflow: ellipsis;
    overflow: hidden;
`;
